import { Injectable } from '@angular/core';
import { ExerciseSet } from '../models/exercise-set.interface';

interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: string;
  allotmentId?: string;
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown';
  isMarkedForDeletion?: boolean;
}

interface DateItem {
  date: Date;
  formattedDate: string;
  dayName: string;
  isExpanded: boolean;
  exercises: Exercise[];
  isRestDay: boolean;
  restDayAllotmentId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExerciseDataService {
  /**
   * Convert time string (MM:SS) to total seconds
   * @param timeString - Time in MM:SS format
   * @returns Total seconds
   */
  static convertTimeToSeconds(timeString: string): number {
    if (!timeString || timeString.trim() === '') {
      return 0;
    }

    const parts = timeString.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      return minutes * 60 + seconds; // Convert MM:SS to total seconds
    }

    // Fallback: treat as seconds if it's just a number
    const numValue = parseInt(timeString);
    return isNaN(numValue) ? 0 : numValue;
  }
  /**
   * Resolve exercise name from exercise ID
   * @param exerciseId - Exercise ID
   * @param availableExercisesData - Array of available exercises
   * @returns Exercise name or ID if not found
   */
  resolveExerciseName(
    exerciseId: string,
    availableExercisesData: any[]
  ): string {
    if (!exerciseId || !availableExercisesData.length) {
      return exerciseId || '';
    }

    const exercise = availableExercisesData.find(
      (ex: any) => ex.id === exerciseId
    );
    return exercise ? exercise.name : exerciseId;
  }
  /**
   * Map API phase to component section
   * @param phase - API phase string
   * @returns Component section
   */
  mapPhaseToSection(phase: string): 'warmup' | 'main' | 'cooldown' {
    if (!phase) return 'main';

    const lowerPhase = phase.toLowerCase();
    if (lowerPhase === 'restday') return 'main'; // Rest day fallback, but shouldn't be used
    if (
      lowerPhase === 'warmup' ||
      lowerPhase.includes('warm') ||
      lowerPhase.includes('up')
    )
      return 'warmup';
    if (
      lowerPhase === 'cooldown' ||
      lowerPhase.includes('cool') ||
      lowerPhase.includes('down')
    )
      return 'cooldown';
    if (lowerPhase === 'main') return 'main';
    return 'main';
  }

  /**
   * Map component section to API phase
   * @param section - Component section
   * @returns API phase string
   */
  mapSectionToPhase(section: 'warmup' | 'main' | 'cooldown'): string {
    const sectionMap = {
      warmup: 'warmup',
      main: 'main',
      cooldown: 'cooldown',
    };
    return sectionMap[section];
  }
  /**
   * Generate exercise array for API submission
   */
  generateExerciseArray(
    dateList: DateItem[],
    availableExercises: any[],
    pendingDeletions: Set<string>
  ): any[] {
    const exerciseArray: any[] = [];

    dateList.forEach((dateItem) => {
      if (dateItem.isRestDay) {
        // Handle rest day
        const restDayEntry = {
          date: this.formatDateForAPI(dateItem.date),
          isRestDay: true,
          allotmentId: dateItem.restDayAllotmentId || null,
          exercises: [],
        };
        exerciseArray.push(restDayEntry);
      } else {
        // Handle workout day - only include active exercises (not marked for deletion)
        const activeExercises = dateItem.exercises.filter(
          (ex) => !ex.isMarkedForDeletion
        );

        if (activeExercises.length > 0) {
          const workoutEntry = {
            date: this.formatDateForAPI(dateItem.date),
            isRestDay: false,
            exercises: activeExercises
              .filter(
                (exercise) =>
                  exercise.exercise && exercise.exercise.trim() !== ''
              )
              .map((exercise) => {
                // Find exercise data from available exercises
                const exerciseData = availableExercises.find(
                  (e) => e.name === exercise.exercise
                );

                const setsData = exercise.sets
                  .filter((set) => {
                    const hasReps =
                      set.reps &&
                      set.reps.toString().trim() !== '' &&
                      set.reps.toString() !== '0';
                    const hasTime =
                      (set.timeMinutes && set.timeMinutes > 0) ||
                      (set.timeSeconds && set.timeSeconds > 0) ||
                      (set.time &&
                        set.time.toString().trim() !== '' &&
                        set.time.toString() !== '0');
                    return hasReps || hasTime;
                  })
                  .map((set) => ({
                    reps:
                      set.reps === '' ||
                      set.reps === null ||
                      set.reps === undefined
                        ? 0
                        : Number(set.reps),
                    time: (set.timeMinutes || 0) * 60 + (set.timeSeconds || 0),
                    weights:
                      set.weights === '' ||
                      set.weights === null ||
                      set.weights === undefined
                        ? 0
                        : Number(set.weights),
                  }));

                return {
                  exerciseId: exerciseData ? exerciseData.id : null,
                  exerciseName: exercise.exercise,
                  phase: this.mapSectionToPhase(exercise.section || 'main'),
                  sets: setsData,
                  allotmentId: exercise.allotmentId || null,
                };
              }),
          };
          exerciseArray.push(workoutEntry);
        }
      }
    });

    return exerciseArray;
  }

  /**
   * Transform API exercise data to component format
   */
  transformAPIExerciseToComponent(apiExercise: any): Exercise {
    const transformedSets: ExerciseSet[] =
      apiExercise.sets?.map((set: any, index: number) => ({
        setNumber: index + 1,
        reps: set.reps?.toString() || '',
        time: this.formatTimeFromSeconds(set.time || 0),
        timeMinutes: Math.floor((set.time || 0) / 60),
        timeSeconds: (set.time || 0) % 60,
        totalSeconds: set.time || 0,
        weights: set.weights?.toString() || '',
        status: 'existing' as const,
      })) || [];

    return {
      id: Math.random(), // Generate temporary ID
      exercise: apiExercise.exerciseName || '',
      exerciseId: apiExercise.exerciseId?.toString() || '',
      allotmentId: apiExercise.allotmentId?.toString() || '',
      sets: transformedSets,
      section: this.mapPhaseToSection(apiExercise.phase || 'Main'),
      isMarkedForDeletion: false,
    };
  }

  /**
   * Create default exercise structure
   */
  createDefaultExercise(
    section: 'warmup' | 'main' | 'cooldown' = 'main'
  ): Exercise {
    return {
      id: Math.random(),
      exercise: '',
      sets: [
        {
          setNumber: 1,
          reps: '',
          time: '',
          timeMinutes: 0,
          timeSeconds: 0,
          weights: '',
          status: 'new',
        },
      ],
      section: section,
      isMarkedForDeletion: false,
    };
  }

  /**
   * Clone exercise with new ID
   */
  cloneExercise(exercise: Exercise): Exercise {
    return {
      ...exercise,
      id: Math.random(),
      allotmentId: undefined, // Clear allotment ID for cloned exercise
      sets: exercise.sets.map((set) => ({
        ...set,
        status: 'new' as const,
      })),
      isMarkedForDeletion: false,
    };
  }

  /**
   * Get section display name
   * @param section - Section type
   * @returns Display name
   */
  getSectionDisplayName(section: 'warmup' | 'main' | 'cooldown'): string {
    const sectionNames = {
      warmup: 'Warm Up',
      main: 'Main Exercise',
      cooldown: 'Cool Down',
    };
    return sectionNames[section];
  }

  /**
   * Format time from total seconds to MM:SS format
   */
  formatTimeFromSeconds(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds === 0) return '';

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Parse time string (MM:SS) to total seconds
   */
  parseTimeToSeconds(timeString: string): number {
    if (!timeString || timeString.trim() === '') return 0;

    const parts = timeString.split(':');
    if (parts.length !== 2) return 0;

    const minutes = parseInt(parts[0]) || 0;
    const seconds = parseInt(parts[1]) || 0;

    return minutes * 60 + seconds;
  }

  /**
   * Format date for API (helper method)
   */
  private formatDateForAPI(date: Date): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.error('Invalid date provided to formatDateForAPI:', date);
      return '';
    }
    return date.toISOString().split('T')[0];
  }

  /**
   * Validate exercise data before submission
   */
  validateExerciseForSubmission(exercise: Exercise): boolean {
    // Must have exercise name
    if (!exercise.exercise || exercise.exercise.trim() === '') {
      return false;
    }

    // Must have at least one valid set
    const hasValidSet = exercise.sets.some((set) => {
      const hasReps = !!(
        set.reps &&
        set.reps.toString().trim() !== '' &&
        set.reps.toString() !== '0'
      );
      const hasTime = !!(
        (set.timeMinutes && set.timeMinutes > 0) ||
        (set.timeSeconds && set.timeSeconds > 0) ||
        (set.time &&
          set.time.toString().trim() !== '' &&
          set.time.toString() !== '0')
      );
      return hasReps || hasTime;
    });

    return hasValidSet;
  }

  /**
   * Clean exercise data (remove empty exercises and sets)
   */
  cleanExerciseData(exercises: Exercise[]): Exercise[] {
    return exercises
      .filter((exercise) => this.validateExerciseForSubmission(exercise))
      .map((exercise) => ({
        ...exercise,
        sets: exercise.sets.filter((set) => {
          const hasReps = !!(
            set.reps &&
            set.reps.toString().trim() !== '' &&
            set.reps.toString() !== '0'
          );
          const hasTime = !!(
            (set.timeMinutes && set.timeMinutes > 0) ||
            (set.timeSeconds && set.timeSeconds > 0) ||
            (set.time &&
              set.time.toString().trim() !== '' &&
              set.time.toString() !== '0')
          );
          return hasReps || hasTime;
        }),
      }));
  }

  /**
   * Create a new exercise set with default values
   * @param setNumber - Set number for the new set
   * @returns New ExerciseSet object
   */
  createNewSet(setNumber: number): ExerciseSet {
    return {
      setNumber: setNumber,
      reps: '',
      time: '',
      timeMinutes: 0,
      timeSeconds: 0,
      weights: '',
      status: 'new',
    };
  }

  /**
   * Add a new set to an exercise
   * @param exercise - Exercise to add set to
   * @returns Updated exercise with new set
   */
  addSetToExercise(exercise: Exercise): Exercise {
    const newSetNumber = exercise.sets.length + 1;
    const newSet = this.createNewSet(newSetNumber);
    exercise.sets.push(newSet);
    return exercise;
  }

  /**
   * Remove a set from an exercise and renumber remaining sets
   * @param exercise - Exercise to remove set from
   * @param setIndex - Index of set to remove
   * @returns Updated exercise with set removed
   */
  removeSetFromExercise(exercise: Exercise, setIndex: number): Exercise {
    if (
      exercise.sets.length > 1 &&
      setIndex >= 0 &&
      setIndex < exercise.sets.length
    ) {
      exercise.sets.splice(setIndex, 1);
      // Renumber remaining sets
      exercise.sets.forEach((set, index) => {
        set.setNumber = index + 1;
      });
    }
    return exercise;
  }

  /**
   * Create a new exercise with default values
   * @param id - Unique ID for the exercise
   * @param section - Exercise section (warmup, main, cooldown)
   * @returns New Exercise object
   */
  createNewExercise(
    id: number,
    section: 'warmup' | 'main' | 'cooldown' = 'main'
  ): Exercise {
    return {
      id: id,
      exercise: '',
      exerciseId: undefined,
      sets: [this.createNewSet(1)],
      section: section,
      isMarkedForDeletion: false,
    };
  }

  /**
   * Get the next available exercise ID from a list of exercises
   * @param exercises - Array of exercises to check
   * @returns Next available ID
   */
  getNextExerciseId(exercises: Exercise[]): number {
    return Math.max(...exercises.map((e) => e.id), 0) + 1;
  }

  /**
   * Add a new exercise to an array of exercises
   * @param exercises - Array to add exercise to
   * @param section - Exercise section
   * @returns Updated exercises array
   */
  addNewExerciseToArray(
    exercises: Exercise[],
    section: 'warmup' | 'main' | 'cooldown' = 'main'
  ): Exercise[] {
    const newId = this.getNextExerciseId(exercises);
    const newExercise = this.createNewExercise(newId, section);
    exercises.push(newExercise);
    return exercises;
  }

  /**
   * Generate a list of date items for a given date range
   * @param startDate - Start date of the range
   * @param endDate - End date of the range
   * @param dateUtilityService - Date utility service for formatting
   * @returns Array of date items with default exercises
   */
  generateDateList(
    startDate: Date,
    endDate: Date,
    dateUtilityService: any
  ): DateItem[] {
    const dateList: DateItem[] = [];
    if (!startDate || !endDate) return dateList;

    // Create dates with timezone-safe handling
    const currentDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    const endDateCopy = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );

    while (currentDate <= endDateCopy) {
      const dateObj = this.createDateItem(currentDate, dateUtilityService);
      dateList.push(dateObj);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateList;
  }

  /**
   * Create a single date item with default exercises
   * @param date - Date for the item
   * @param dateUtilityService - Date utility service for formatting
   * @returns Date item object
   */
  private createDateItem(date: Date, dateUtilityService: any): DateItem {
    const safeDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    return {
      date: safeDate,
      formattedDate: dateUtilityService.formatDateForDisplay(safeDate),
      dayName: dateUtilityService.getDayName(safeDate),
      isExpanded: false,
      exercises: this.createDefaultExercisesForDate(),
      isRestDay: false,
    };
  }

  /**
   * Create default exercises for a new date
   * @returns Array of default exercises for warmup, main, and cooldown
   */
  private createDefaultExercisesForDate(): Exercise[] {
    return [
      this.createNewExercise(1, 'warmup'),
      this.createNewExercise(2, 'main'),
      this.createNewExercise(3, 'cooldown'),
    ];
  }

  /**
   * Undo the conversion of a workout day to rest day by restoring marked exercises
   * @param dateItem - The date item to undo
   * @returns boolean indicating if undo was successful
   */
  undoRestDayConversion(dateItem: DateItem): boolean {
    if (!dateItem.isRestDay) {
      console.log('Cannot undo: date is not currently a rest day');
      return false;
    }

    // Check if there are any exercises marked for deletion to restore
    const markedExercises = dateItem.exercises.filter(
      (ex) => ex.isMarkedForDeletion
    );
    if (markedExercises.length === 0) {
      console.log('No exercises to restore for', dateItem.formattedDate);
      return false;
    }

    console.log(
      `Undoing rest day conversion for ${dateItem.formattedDate} - restoring ${markedExercises.length} exercises`
    );

    // Unmark all exercises for deletion
    dateItem.exercises.forEach((exercise) => {
      if (exercise.isMarkedForDeletion) {
        exercise.isMarkedForDeletion = false;
        console.log(
          `Restored exercise: ${exercise.exercise} (ID: ${exercise.allotmentId})`
        );
      }
    });

    // Remove the empty rest day exercise and convert back to workout day
    dateItem.exercises = dateItem.exercises.filter(
      (ex) => ex.exercise.trim() !== '' || ex.allotmentId // Keep exercises with content or allotment IDs
    );

    // Convert back to workout day
    dateItem.isRestDay = false;

    console.log(
      'Exercises after undo:',
      dateItem.exercises.map((ex) => ({
        name: ex.exercise,
        allotmentId: ex.allotmentId,
        isMarkedForDeletion: ex.isMarkedForDeletion,
      }))
    );

    return true;
  }

  /**
   * Check if a date can be undone (has exercises marked for deletion)
   * @param dateItem - The date item to check
   * @returns true if the date can be undone
   */
  canUndoRestDayConversion(dateItem: DateItem): boolean {
    return (
      dateItem.isRestDay &&
      dateItem.exercises.some((ex) => ex.isMarkedForDeletion)
    );
  }
}
